import { REACTION_META } from '../../shared/schemas';

/**
 * The whole game in about a screen.
 *
 * The old How It Works was seven cards of bullet lists, and the two rules that
 * actually change what a player does (one pun a day, one crown a day) were
 * buried in the middle of it. Those two are now the first thing on the page.
 */
function Rules() {
  return (
    <div>
      <h1 className="font-display text-2xl font-bold">How it works</h1>
      <p className="mt-2 text-ink/60">
        One prompt a day. Write a pun for it, then read everyone else&rsquo;s. Some days
        you&rsquo;ll nail it. Some days you&rsquo;ll post something and immediately wish you
        hadn&rsquo;t. Both count.
      </p>

      <div className="mt-7 grid gap-3 sm:grid-cols-2">
        <BigRule colour="bg-coral" n="1">
          One pun per day, per person. Make it count.
        </BigRule>
        <BigRule colour="bg-grape" n="2">
          One crown per day to give away. Spend it on the best.
        </BigRule>
      </div>

      <Section title="The prompt">
        <p>
          Every midnight Pacific, a new pairing goes up: something to make, and a theme to make it
          about. Everyone gets the same one.
        </p>
      </Section>

      <Section title="Reactions">
        <p className="mb-4">
          Four ways to respond, and you pick one per pun. Tap it again to take it back.
        </p>
        <ul className="space-y-2.5">
          {REACTION_META.map(({ emoji, name, label }) => (
            <li key={name} className="flex items-baseline gap-3">
              <span className="text-xl" aria-hidden="true">
                {emoji}
              </span>
              <span>
                <span className="font-semibold">{label}</span>
                {name === 'crown' && (
                  <span className="text-ink/60"> is the one you only get once a day.</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Friends">
        <p>
          Following someone puts their puns in your Friends feed. It is one-way, so they do not have
          to follow you back, and there is nothing to accept.
        </p>
      </Section>

      <Section title="Keep it friendly">
        <p>
          Family-friendly, original, and aimed at the prompt rather than at each other. Groans are
          the goal. Genuine offence is not. That is the whole code of conduct.
        </p>
      </Section>

      <Section title="Put it on your phone">
        <p className="mb-3">
          It works as an app if you add it to your home screen, which is also the only way iOS will
          deliver notifications.
        </p>
        <p className="mb-1.5">
          <span className="font-semibold">iPhone:</span> open in Safari, tap Share, then Add to Home
          Screen.
        </p>
        <p>
          <span className="font-semibold">Android:</span> open in Chrome, tap the three-dot menu,
          then Add to Home screen.
        </p>
      </Section>
    </div>
  );
}

function BigRule({ n, colour, children }: { n: string; colour: string; children: string }) {
  return (
    <div className={`rounded-2xl border-2 border-ink px-5 py-5 text-white shadow-pop ${colour}`}>
      <span className="font-display text-3xl font-extrabold opacity-60">{n}</span>
      <p className="mt-1 font-display text-lg font-bold leading-snug">{children}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8 border-t-2 border-dashed border-ink/20 pt-6">
      <h2 className="mb-2 font-display text-lg font-bold">{title}</h2>
      <div className="text-ink/75">{children}</div>
    </section>
  );
}

export default Rules;
